import { calculateHaversineDistance } from '@/services/trackMetricsCalculationService'
import type { Segment, Waypoint } from '@/types/tour'
import { resolveTrackGradientBucket } from '@/utils/trackGradientSegments'

export type TrackProfileMetricId = 'gradient' | 'surface' | 'wayType'
export type TrackProfileMetricOption = {
  id: TrackProfileMetricId
  label: string
}

export const TRACK_PROFILE_METRICS: TrackProfileMetricOption[] = [
  { id: 'gradient', label: 'Gradient' },
  { id: 'surface', label: 'Surface' },
  { id: 'wayType', label: 'Way type' },
]

export type TrackProfilePoint = {
  index: number
  lat: number
  lon: number
  ele: number | null
  distanceM: number
  timeS: number | null
  wayTags: string | null
  grade: number | null
}

export type TrackProfileCategory = {
  id: string
  label: string
  color: string
  rawValue: string | null
}

export type TrackProfileLeg = {
  start: TrackProfilePoint
  end: TrackProfilePoint
  distanceM: number
  startDistanceM: number
  endDistanceM: number
  startTimeS: number | null
  endTimeS: number | null
  color: string
  categoryId: string
  categoryLabel: string
  rawValue: string | null
  coordinates: [[number, number], [number, number]]
}

export type TrackProfileMergedSegment = {
  start: TrackProfilePoint
  end: TrackProfilePoint
  distanceM: number
  startDistanceM: number
  endDistanceM: number
  startTimeS: number | null
  endTimeS: number | null
  color: string
  categoryId: string
  categoryLabel: string
  rawValue: string | null
  coordinates: [number, number][]
  legs: TrackProfileLeg[]
}

export type TrackProfileLegendItem = {
  id: string
  label: string
  color: string
  distanceM: number
}

export type TrackProfileBreakdownItem = TrackProfileLegendItem & {
  percent: number
}

export type TrackProfileAnalysis = {
  metricId: TrackProfileMetricId
  points: TrackProfilePoint[]
  legs: TrackProfileLeg[]
  legend: TrackProfileLegendItem[]
  totalDistanceM: number
  totalTimeS: number | null
  elevationMin: number | null
  elevationMax: number | null
  elevationGain: number | null
  elevationLoss: number | null
  hasElevation: boolean
}

export type TrackProfileAnalyses = Record<TrackProfileMetricId, TrackProfileAnalysis>
export type TrackProfileVisibleRange = {
  start: number
  end: number
}

export type TrackProfileFeatureProperties = {
  color: string
  categoryId: string
  categoryLabel: string
  rawValue: string | null
  distanceM: number
  startDistanceM: number
  endDistanceM: number
}

export type TrackProfileLocationInput = {
  lat: number
  lon: number
  accuracyM?: number | null
}

export type TrackProfileLocationMatch = {
  distanceM: number
  elevationM: number | null
  distanceFromTrackM: number
  toleranceM: number
  accuracyM: number | null
}

type WayTagMap = Record<string, string>
type ProfileWaypoint = Waypoint & {
  timeOffset?: number
  wayTags?: string
  nodeTags?: string
}
type TrackProfileLegBase = {
  start: TrackProfilePoint
  end: TrackProfilePoint
  distanceM: number
  startDistanceM: number
  endDistanceM: number
  startTimeS: number | null
  endTimeS: number | null
}
type TrackProfileBase = {
  points: TrackProfilePoint[]
  legBases: TrackProfileLegBase[]
  totalDistanceM: number
  totalTimeS: number | null
  elevationMin: number | null
  elevationMax: number | null
  elevationGain: number | null
  elevationLoss: number | null
  hasElevation: boolean
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const EARTH_RADIUS_M = 6371e3
const DEG_TO_RAD = Math.PI / 180
const MIN_DISTANCE_METERS = 1
const TRACK_PROFILE_LOCATION_BASE_TOLERANCE_M = 45
const TRACK_PROFILE_LOCATION_MAX_ACCURACY_BUFFER_M = 60

const SURFACE_COLORS = {
  paved: '#2563eb',
  compacted: '#14b8a6',
  gravel: '#d97706',
  natural: '#8b5a2b',
  rough: '#7c3aed',
  unknown: '#94a3b8',
} as const

const WAY_TYPE_COLORS = {
  cycleway: '#16a34a',
  localRoad: '#2563eb',
  mainRoad: '#dc2626',
  trail: '#d97706',
  pedestrian: '#7c3aed',
  unknown: '#94a3b8',
} as const

const PAVED_SURFACES = new Set([
  'asphalt',
  'concrete',
  'concrete:lanes',
  'concrete:plates',
  'paved',
  'paving_stones',
])

const COMPACTED_SURFACES = new Set(['compacted', 'fine_gravel'])
const GRAVEL_SURFACES = new Set(['gravel', 'pebblestone'])
const NATURAL_SURFACES = new Set([
  'unpaved',
  'dirt',
  'earth',
  'ground',
  'sand',
  'grass',
  'grass_paver',
])
const ROUGH_SURFACES = new Set(['mud', 'cobblestone', 'sett', 'rock', 'stones', 'wood', 'metal'])

const CYCLEWAYS = new Set(['cycleway'])
const LOCAL_ROADS = new Set([
  'residential',
  'living_street',
  'service',
  'unclassified',
  'tertiary',
  'tertiary_link',
  'road',
])
const MAIN_ROADS = new Set([
  'primary',
  'primary_link',
  'secondary',
  'secondary_link',
  'trunk',
  'trunk_link',
  'motorway',
  'motorway_link',
])
const TRAILS = new Set(['path', 'track', 'bridleway', 'cycleway:lane'])
const PEDESTRIAN_WAYS = new Set(['footway', 'pedestrian', 'steps'])

const gradientLabelByBucketId: Record<string, string> = {
  steepDownhill: 'Steep downhill',
  downhill: 'Downhill',
  gentleDownhill: 'Gentle downhill',
  flat: 'Flat / easy',
  gentleUphill: 'Gentle uphill',
  moderateUphill: 'Moderate uphill',
  steepUphill: 'Steep uphill',
}

function normalizeMetricId(metricId: string | null | undefined): TrackProfileMetricId {
  return TRACK_PROFILE_METRICS.some((metric) => metric.id === metricId)
    ? (metricId as TrackProfileMetricId)
    : 'gradient'
}

function areSameCoordinate(a: Pick<Waypoint, 'lat' | 'lon'>, b: Pick<Waypoint, 'lat' | 'lon'>) {
  return a.lat === b.lat && a.lon === b.lon
}

function normalizeElevation(value: number | null | undefined) {
  return isFiniteNumber(value) ? value : null
}

function normalizeTime(
  point: ProfileWaypoint,
  localDistanceM: number,
  segmentDistanceM: number,
  segmentStartTimeS: number,
  segmentDurationS: number | null,
) {
  const timeOffset = isFiniteNumber(point.time_offset)
    ? point.time_offset
    : isFiniteNumber(point.timeOffset)
      ? point.timeOffset
      : null

  if (timeOffset !== null) {
    return segmentStartTimeS + timeOffset
  }

  if (segmentDurationS !== null && segmentDistanceM > 0) {
    return segmentStartTimeS + (localDistanceM / segmentDistanceM) * segmentDurationS
  }

  return null
}

function getLegGradePercent(
  start: TrackProfilePoint,
  end: TrackProfilePoint,
  distanceMeters: number,
): number | null {
  if (!Number.isFinite(distanceMeters) || distanceMeters < MIN_DISTANCE_METERS) {
    return null
  }

  if (isFiniteNumber(start.ele) && isFiniteNumber(end.ele)) {
    return ((end.ele - start.ele) / distanceMeters) * 100
  }

  if (isFiniteNumber(end.grade)) return end.grade
  if (isFiniteNumber(start.grade)) return start.grade
  return null
}

function getPointWayTags(point: ProfileWaypoint): string | null {
  return point.way_tags ?? point.wayTags ?? null
}

function getPointGrade(point: ProfileWaypoint): number | null {
  if (isFiniteNumber(point.grade)) return point.grade
  return null
}

export function parseWayTags(rawWayTags: string | null | undefined): WayTagMap {
  if (!rawWayTags) return {}

  return rawWayTags
    .trim()
    .replace(/[,;|]+/g, ' ')
    .split(/\s+/)
    .reduce<WayTagMap>((tags, token) => {
      const separatorIndex = token.indexOf('=')
      if (separatorIndex < 0) {
        tags[token] = 'yes'
        return tags
      }

      const key = token.slice(0, separatorIndex)
      const value = token.slice(separatorIndex + 1)
      if (!key || !value) return tags
      tags[key] = value
      return tags
    }, {})
}

function getTrackProfileLocationTolerance(accuracyM: number | null | undefined) {
  const accuracyBuffer = isFiniteNumber(accuracyM)
    ? Math.min(Math.max(accuracyM, 0), TRACK_PROFILE_LOCATION_MAX_ACCURACY_BUFFER_M)
    : 0
  return TRACK_PROFILE_LOCATION_BASE_TOLERANCE_M + accuracyBuffer
}

function interpolateNullableNumber(start: number | null, end: number | null, t: number) {
  if (isFiniteNumber(start) && isFiniteNumber(end)) {
    return start + (end - start) * t
  }

  if (t < 0.5 && isFiniteNumber(start)) return start
  if (isFiniteNumber(end)) return end
  if (isFiniteNumber(start)) return start
  return null
}

function projectLocationToProfileSegment(
  location: TrackProfileLocationInput,
  start: TrackProfilePoint,
  end: TrackProfilePoint,
) {
  const originLatRad = start.lat * DEG_TO_RAD
  const toLocalMeters = (lat: number, lon: number) => ({
    x: (lon - start.lon) * DEG_TO_RAD * Math.cos(originLatRad) * EARTH_RADIUS_M,
    y: (lat - start.lat) * DEG_TO_RAD * EARTH_RADIUS_M,
  })

  const projectedEnd = toLocalMeters(end.lat, end.lon)
  const projectedLocation = toLocalMeters(location.lat, location.lon)
  const segmentLengthSq = projectedEnd.x ** 2 + projectedEnd.y ** 2
  if (segmentLengthSq <= 0) return null

  const t = Math.max(
    0,
    Math.min(
      1,
      (projectedLocation.x * projectedEnd.x + projectedLocation.y * projectedEnd.y) /
        segmentLengthSq,
    ),
  )
  const nearestX = projectedEnd.x * t
  const nearestY = projectedEnd.y * t
  const distanceFromTrackM = Math.hypot(
    projectedLocation.x - nearestX,
    projectedLocation.y - nearestY,
  )

  return {
    distanceM: start.distanceM + (end.distanceM - start.distanceM) * t,
    elevationM: interpolateNullableNumber(start.ele, end.ele, t),
    distanceFromTrackM,
  }
}

export function findCurrentLocationOnTrackProfile(
  analysis: Pick<TrackProfileAnalysis, 'points'>,
  location: TrackProfileLocationInput | null | undefined,
): TrackProfileLocationMatch | null {
  if (!location || !isFiniteNumber(location.lat) || !isFiniteNumber(location.lon)) return null
  if (analysis.points.length < 2) return null

  const toleranceM = getTrackProfileLocationTolerance(location.accuracyM)
  let bestMatch: Omit<TrackProfileLocationMatch, 'toleranceM' | 'accuracyM'> | null = null

  for (let index = 1; index < analysis.points.length; index += 1) {
    const start = analysis.points[index - 1]
    const end = analysis.points[index]
    const match = projectLocationToProfileSegment(location, start, end)
    if (!match) continue

    if (!bestMatch || match.distanceFromTrackM < bestMatch.distanceFromTrackM) {
      bestMatch = match
    }
  }

  if (!bestMatch || bestMatch.distanceFromTrackM > toleranceM) return null

  return {
    ...bestMatch,
    toleranceM,
    accuracyM: isFiniteNumber(location.accuracyM) ? location.accuracyM : null,
  }
}

function resolveGradientCategory(
  start: TrackProfilePoint,
  end: TrackProfilePoint,
  distanceM: number,
  fallbackColor: string,
): TrackProfileCategory {
  const gradePercent = getLegGradePercent(start, end, distanceM)
  if (gradePercent === null) {
    return {
      id: 'fallback',
      label: 'Unknown',
      color: fallbackColor,
      rawValue: null,
    }
  }

  const bucket = resolveTrackGradientBucket(gradePercent)
  return {
    id: bucket.id,
    label: gradientLabelByBucketId[bucket.id] ?? bucket.id,
    color: bucket.color,
    rawValue: `${gradePercent.toFixed(1)}%`,
  }
}

function resolveSurfaceCategory(tags: WayTagMap): TrackProfileCategory {
  const surface = tags.surface?.toLowerCase()
  const tracktype = tags.tracktype?.toLowerCase()

  if (surface && PAVED_SURFACES.has(surface)) {
    return { id: 'paved', label: 'Paved', color: SURFACE_COLORS.paved, rawValue: surface }
  }
  if ((surface && COMPACTED_SURFACES.has(surface)) || (!surface && tracktype === 'grade1')) {
    return {
      id: 'compacted',
      label: 'Compacted',
      color: SURFACE_COLORS.compacted,
      rawValue: surface ?? tracktype ?? null,
    }
  }
  if (
    (surface && GRAVEL_SURFACES.has(surface)) ||
    (!surface && (tracktype === 'grade2' || tracktype === 'grade3'))
  ) {
    return {
      id: 'gravel',
      label: 'Gravel',
      color: SURFACE_COLORS.gravel,
      rawValue: surface ?? tracktype ?? null,
    }
  }
  if (
    (surface && NATURAL_SURFACES.has(surface)) ||
    (!surface && (tracktype === 'grade4' || tracktype === 'grade5'))
  ) {
    return {
      id: 'natural',
      label: 'Natural',
      color: SURFACE_COLORS.natural,
      rawValue: surface ?? tracktype ?? null,
    }
  }
  if (surface && ROUGH_SURFACES.has(surface)) {
    return { id: 'rough', label: 'Rough', color: SURFACE_COLORS.rough, rawValue: surface }
  }

  return {
    id: 'unknown',
    label: 'Unknown',
    color: SURFACE_COLORS.unknown,
    rawValue: surface ?? tracktype ?? null,
  }
}

function resolveWayTypeCategory(tags: WayTagMap): TrackProfileCategory {
  const highway = tags.highway?.toLowerCase()
  const cycleway = tags.cycleway?.toLowerCase()

  if (highway && CYCLEWAYS.has(highway)) {
    return {
      id: 'cycleway',
      label: 'Cycleway',
      color: WAY_TYPE_COLORS.cycleway,
      rawValue: highway,
    }
  }
  if (cycleway && cycleway !== 'no') {
    return {
      id: 'cycleway',
      label: 'Cycleway',
      color: WAY_TYPE_COLORS.cycleway,
      rawValue: cycleway,
    }
  }
  if (highway && PEDESTRIAN_WAYS.has(highway)) {
    return {
      id: 'pedestrian',
      label: 'Pedestrian',
      color: WAY_TYPE_COLORS.pedestrian,
      rawValue: highway,
    }
  }
  if (highway && TRAILS.has(highway)) {
    return {
      id: 'trail',
      label: 'Trail / track',
      color: WAY_TYPE_COLORS.trail,
      rawValue: highway,
    }
  }
  if (highway && LOCAL_ROADS.has(highway)) {
    return {
      id: 'localRoad',
      label: 'Local road',
      color: WAY_TYPE_COLORS.localRoad,
      rawValue: highway,
    }
  }
  if (highway && MAIN_ROADS.has(highway)) {
    return {
      id: 'mainRoad',
      label: 'Main road',
      color: WAY_TYPE_COLORS.mainRoad,
      rawValue: highway,
    }
  }

  return {
    id: 'unknown',
    label: 'Unknown',
    color: WAY_TYPE_COLORS.unknown,
    rawValue: highway ?? cycleway ?? null,
  }
}

function resolveMetricCategory(
  metricId: TrackProfileMetricId,
  start: TrackProfilePoint,
  end: TrackProfilePoint,
  distanceM: number,
  fallbackColor: string,
): TrackProfileCategory {
  if (metricId === 'gradient') {
    return resolveGradientCategory(start, end, distanceM, fallbackColor)
  }

  const tags = parseWayTags(end.wayTags ?? start.wayTags)
  if (metricId === 'surface') {
    return resolveSurfaceCategory(tags)
  }

  return resolveWayTypeCategory(tags)
}

function buildProfilePoints(segments: Segment[]): {
  points: TrackProfilePoint[]
  totalDistanceM: number
  totalTimeS: number | null
} {
  const points: TrackProfilePoint[] = []
  let totalDistanceM = 0
  let totalTimeS = 0
  let hasTime = false

  for (const segment of segments) {
    if (!segment.points || segment.points.length === 0) continue

    const localDistances: number[] = new Array(segment.points.length).fill(0)
    for (let pointIndex = 1; pointIndex < segment.points.length; pointIndex += 1) {
      const start = segment.points[pointIndex - 1]
      const end = segment.points[pointIndex]
      localDistances[pointIndex] =
        localDistances[pointIndex - 1] +
        calculateHaversineDistance(start.lat, start.lon, end.lat, end.lon)
    }

    const segmentDistanceM =
      localDistances[localDistances.length - 1] ||
      (isFiniteNumber(segment.trackLength) ? segment.trackLength : 0)
    const segmentDurationS =
      isFiniteNumber(segment.totalTime) && segment.totalTime >= 0 ? segment.totalTime : null
    const segmentStartTimeS = totalTimeS

    for (let pointIndex = 0; pointIndex < segment.points.length; pointIndex += 1) {
      const point = segment.points[pointIndex] as ProfileWaypoint
      const distanceM = totalDistanceM + localDistances[pointIndex]
      const timeS = normalizeTime(
        point,
        localDistances[pointIndex],
        segmentDistanceM,
        segmentStartTimeS,
        segmentDurationS,
      )

      if (
        pointIndex === 0 &&
        points.length > 0 &&
        areSameCoordinate(points[points.length - 1], point)
      ) {
        const previousPoint = points[points.length - 1]
        previousPoint.ele = previousPoint.ele ?? normalizeElevation(point.ele)
        previousPoint.timeS = previousPoint.timeS ?? timeS
        previousPoint.wayTags = previousPoint.wayTags ?? getPointWayTags(point)
        previousPoint.grade = previousPoint.grade ?? getPointGrade(point)
        continue
      }

      points.push({
        index: points.length,
        lat: point.lat,
        lon: point.lon,
        ele: normalizeElevation(point.ele),
        distanceM,
        timeS,
        wayTags: getPointWayTags(point),
        grade: getPointGrade(point),
      })
    }

    totalDistanceM += segmentDistanceM
    if (segmentDurationS !== null) {
      totalTimeS += segmentDurationS
      hasTime = true
    } else {
      const lastPointTime = points[points.length - 1]?.timeS
      if (isFiniteNumber(lastPointTime)) {
        totalTimeS = lastPointTime
        hasTime = true
      }
    }
  }

  return {
    points,
    totalDistanceM,
    totalTimeS: hasTime ? totalTimeS : null,
  }
}

function buildProfileBase(segments: Segment[]): TrackProfileBase {
  const { points, totalDistanceM, totalTimeS } = buildProfilePoints(segments)
  const elevations = points
    .map((point) => point.ele)
    .filter((value): value is number => isFiniteNumber(value))
  const elevationMin =
    elevations.length > 0 ? Math.min(...elevations.map((value) => Math.round(value))) : null
  const elevationMax =
    elevations.length > 0 ? Math.max(...elevations.map((value) => Math.round(value))) : null

  let elevationGain: number | null = null
  let elevationLoss: number | null = null
  if (elevations.length > 1) {
    let gain = 0
    let loss = 0
    for (let elevationIndex = 1; elevationIndex < elevations.length; elevationIndex += 1) {
      const delta = elevations[elevationIndex] - elevations[elevationIndex - 1]
      if (delta > 0) gain += delta
      else loss -= delta
    }
    elevationGain = Math.round(gain)
    elevationLoss = Math.round(loss)
  }

  const legBases: TrackProfileLegBase[] = []
  for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
    const start = points[pointIndex - 1]
    const end = points[pointIndex]
    const legDistanceM = end.distanceM - start.distanceM
    if (!Number.isFinite(legDistanceM) || legDistanceM < MIN_DISTANCE_METERS) {
      continue
    }

    legBases.push({
      start,
      end,
      distanceM: legDistanceM,
      startDistanceM: start.distanceM,
      endDistanceM: end.distanceM,
      startTimeS: start.timeS,
      endTimeS: end.timeS,
    })
  }

  return {
    points,
    legBases,
    totalDistanceM,
    totalTimeS,
    elevationMin,
    elevationMax,
    elevationGain,
    elevationLoss,
    hasElevation: elevations.length > 0,
  }
}

function buildTrackProfileAnalysisFromBase(
  profileBase: TrackProfileBase,
  metricId: TrackProfileMetricId,
  fallbackColor: string,
): TrackProfileAnalysis {
  const normalizedMetricId = normalizeMetricId(metricId)
  const legs: TrackProfileLeg[] = profileBase.legBases.map((legBase) => {
    const category = resolveMetricCategory(
      normalizedMetricId,
      legBase.start,
      legBase.end,
      legBase.distanceM,
      fallbackColor,
    )

    return {
      ...legBase,
      color: category.color,
      categoryId: category.id,
      categoryLabel: category.label,
      rawValue: category.rawValue,
      coordinates: [
        [legBase.start.lon, legBase.start.lat],
        [legBase.end.lon, legBase.end.lat],
      ],
    }
  })

  const legendMap = new Map<string, TrackProfileLegendItem>()
  for (const leg of legs) {
    const existing = legendMap.get(leg.categoryId)
    if (existing) {
      existing.distanceM += leg.distanceM
      continue
    }

    legendMap.set(leg.categoryId, {
      id: leg.categoryId,
      label: leg.categoryLabel,
      color: leg.color,
      distanceM: leg.distanceM,
    })
  }

  return {
    metricId: normalizedMetricId,
    points: profileBase.points,
    legs,
    legend: Array.from(legendMap.values()),
    totalDistanceM: profileBase.totalDistanceM,
    totalTimeS: profileBase.totalTimeS,
    elevationMin: profileBase.elevationMin,
    elevationMax: profileBase.elevationMax,
    elevationGain: profileBase.elevationGain,
    elevationLoss: profileBase.elevationLoss,
    hasElevation: profileBase.hasElevation,
  }
}

export function buildTrackProfileAnalyses(
  segments: Segment[],
  fallbackColor: string = '#3b82f6',
): TrackProfileAnalyses {
  const profileBase = buildProfileBase(segments)

  return {
    gradient: buildTrackProfileAnalysisFromBase(profileBase, 'gradient', fallbackColor),
    surface: buildTrackProfileAnalysisFromBase(profileBase, 'surface', fallbackColor),
    wayType: buildTrackProfileAnalysisFromBase(profileBase, 'wayType', fallbackColor),
  }
}

export function buildTrackProfileAnalysis(
  segments: Segment[],
  metricId: TrackProfileMetricId,
  fallbackColor: string = '#3b82f6',
): TrackProfileAnalysis {
  return buildTrackProfileAnalyses(segments, fallbackColor)[normalizeMetricId(metricId)]
}

/**
 * Legend items sorted by share of the route (longest first), each augmented with the
 * fraction of total distance it covers. Used to render the stacked proportion bar and
 * the breakdown list in the route profile panel.
 */
export function getTrackProfileBreakdown(
  analysis: TrackProfileAnalysis,
): TrackProfileBreakdownItem[] {
  const totalDistanceM = analysis.legend.reduce((sum, item) => sum + item.distanceM, 0)
  if (totalDistanceM <= 0) return []

  return analysis.legend
    .map((item) => ({ ...item, percent: item.distanceM / totalDistanceM }))
    .sort((a, b) => b.distanceM - a.distanceM)
}

export function buildTrackProfileLegFeatureCollection(
  legs: TrackProfileLeg[],
): GeoJSON.FeatureCollection<GeoJSON.LineString, TrackProfileFeatureProperties> {
  const mergedSegments = mergeTrackProfileLegs(legs)
  const features: Array<GeoJSON.Feature<GeoJSON.LineString, TrackProfileFeatureProperties>> = []
  for (const mergedSegment of mergedSegments) {
    features.push({
      type: 'Feature',
      properties: {
        color: mergedSegment.color,
        categoryId: mergedSegment.categoryId,
        categoryLabel: mergedSegment.categoryLabel,
        rawValue: mergedSegment.rawValue,
        distanceM: mergedSegment.distanceM,
        startDistanceM: mergedSegment.startDistanceM,
        endDistanceM: mergedSegment.endDistanceM,
      },
      geometry: {
        type: 'LineString',
        coordinates: mergedSegment.coordinates,
      },
    })
  }

  return {
    type: 'FeatureCollection',
    features,
  }
}

export function mergeTrackProfileLegs(legs: TrackProfileLeg[]): TrackProfileMergedSegment[] {
  const mergedSegments: TrackProfileMergedSegment[] = []

  for (const leg of legs) {
    const activeSegment = mergedSegments[mergedSegments.length - 1]
    const canExtendActiveSegment =
      activeSegment &&
      activeSegment.categoryId === leg.categoryId &&
      activeSegment.color === leg.color &&
      activeSegment.coordinates[activeSegment.coordinates.length - 1][0] ===
        leg.coordinates[0][0] &&
      activeSegment.coordinates[activeSegment.coordinates.length - 1][1] === leg.coordinates[0][1]

    if (canExtendActiveSegment) {
      activeSegment.end = leg.end
      activeSegment.distanceM += leg.distanceM
      activeSegment.endDistanceM = leg.endDistanceM
      activeSegment.endTimeS = leg.endTimeS
      activeSegment.coordinates.push(leg.coordinates[1])
      activeSegment.legs.push(leg)
      continue
    }

    mergedSegments.push({
      start: leg.start,
      end: leg.end,
      distanceM: leg.distanceM,
      startDistanceM: leg.startDistanceM,
      endDistanceM: leg.endDistanceM,
      startTimeS: leg.startTimeS,
      endTimeS: leg.endTimeS,
      color: leg.color,
      categoryId: leg.categoryId,
      categoryLabel: leg.categoryLabel,
      rawValue: leg.rawValue,
      coordinates: [leg.coordinates[0], leg.coordinates[1]],
      legs: [leg],
    })
  }

  return mergedSegments
}

export function doesTrackProfileLegOverlapRange(
  leg: TrackProfileLeg,
  visibleRange: TrackProfileVisibleRange,
): boolean {
  return leg.endDistanceM >= visibleRange.start && leg.startDistanceM <= visibleRange.end
}

export function partitionTrackProfileLegsByRange(
  legs: TrackProfileLeg[],
  visibleRange: TrackProfileVisibleRange | null | undefined,
): {
  activeLegs: TrackProfileLeg[]
  inactiveLegs: TrackProfileLeg[]
} {
  if (!visibleRange) {
    return {
      activeLegs: legs,
      inactiveLegs: [],
    }
  }

  const activeLegs: TrackProfileLeg[] = []
  const inactiveLegs: TrackProfileLeg[] = []

  for (const leg of legs) {
    if (doesTrackProfileLegOverlapRange(leg, visibleRange)) {
      activeLegs.push(leg)
    } else {
      inactiveLegs.push(leg)
    }
  }

  return {
    activeLegs,
    inactiveLegs,
  }
}

export function buildTrackProfileFeatureCollection(
  analysis: TrackProfileAnalysis,
): GeoJSON.FeatureCollection<GeoJSON.LineString, TrackProfileFeatureProperties> {
  return buildTrackProfileLegFeatureCollection(analysis.legs)
}

export function findNearestTrackProfilePoint(
  points: TrackProfilePoint[],
  distanceM: number,
): TrackProfilePoint | null {
  if (points.length === 0) return null

  let low = 0
  let high = points.length - 1
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (points[middle].distanceM < distanceM) {
      low = middle + 1
    } else {
      high = middle
    }
  }

  if (low === 0) return points[0]

  const candidate = points[low]
  const previous = points[low - 1]
  return Math.abs(candidate.distanceM - distanceM) < Math.abs(previous.distanceM - distanceM)
    ? candidate
    : previous
}

export function findTrackProfilePositionAtDistance(
  points: TrackProfilePoint[],
  distanceM: number,
): TrackProfilePoint | null {
  if (points.length === 0) return null
  if (points.length === 1) return points[0]

  const clampedDistanceM = Math.max(
    points[0].distanceM,
    Math.min(distanceM, points[points.length - 1].distanceM),
  )

  let low = 0
  let high = points.length - 1
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (points[middle].distanceM < clampedDistanceM) {
      low = middle + 1
    } else {
      high = middle
    }
  }

  if (low === 0) return points[0]

  const end = points[low]
  const start = points[low - 1]
  const segmentDistanceM = end.distanceM - start.distanceM
  if (segmentDistanceM <= 0) {
    return Math.abs(end.distanceM - clampedDistanceM) < Math.abs(start.distanceM - clampedDistanceM)
      ? end
      : start
  }

  const t = (clampedDistanceM - start.distanceM) / segmentDistanceM
  return {
    index: start.index + (end.index - start.index) * t,
    lat: start.lat + (end.lat - start.lat) * t,
    lon: start.lon + (end.lon - start.lon) * t,
    ele: interpolateNullableNumber(start.ele, end.ele, t),
    distanceM: clampedDistanceM,
    timeS: interpolateNullableNumber(start.timeS, end.timeS, t),
    wayTags: t < 0.5 ? start.wayTags : end.wayTags,
    grade: interpolateNullableNumber(start.grade, end.grade, t),
  }
}
